import { PostgrestError } from "@supabase/supabase-js";
import { Notification } from "providers/NotifierProvider";
import { supabase } from "supabase/client";
import { Database } from "supabase/types";
import logSentryError from "utils/logSentryError";

type DbGroup = Database["public"]["Tables"]["groups"];
export interface GroupRow extends Omit<DbGroup["Row"], "roles"> {
  roles: GroupRoles;
}
export interface GroupInsert extends Omit<DbGroup["Insert"], "roles"> {
  roles?: GroupRoles;
}
export interface GroupUpdate extends Omit<DbGroup["Update"], "roles"> {
  roles?: GroupRoles;
}

export type GroupRole =
  | "manage-events"
  | "manage-rooms"
  | "manage-money"
  | "manage-secret-santa"
  | "manage-invite-link";
export type GroupRoles = Partial<Record<GroupRole, number[]>>;

export type GetAllGroups = Pick<GroupRow, "id" | "name" | "avatar" | "users" | "admins" | "roles">;
class GroupApi {
  showNotification?: (notification: Notification) => void;

  constructor(showNotification?: (notification: Notification) => void) {
    this.showNotification = showNotification;
  }

  /**
   * Get all groups in which the authenticated user is member
   *
   * @param userId
   * @returns
   */
  async getAllGroups(userId: number): Promise<{
    groups: GetAllGroups[];
    error: PostgrestError | null;
  }> {
    const { data: groups, error } = await supabase
      .from("groups")
      .select(
        `
        id, 
        name,
        avatar,
        users,
        admins,
        roles
        `
      )
      .or(`admins.cs.{${userId}},users.cs.{${userId}}`)
      .order("name");

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load groups",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_API: getAllGroups");
      return { groups: [], error };
    }

    return {
      groups: (groups ?? []) as unknown as GetAllGroups[],
      error: null,
    };
  }

  /**
   * Create new group
   *
   * @param name
   * @param admin
   * @param users
   * @returns
   */
  async createGroup(name: string, admin: number, users: number[]): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.from("groups").insert({
      name,
      admins: [admin],
      users: [...users, admin],
    });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to create group.",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_API: createGroup");
      return { error };
    }

    return { error: null };
  }

  /**
   * Get group by id
   *
   * @param groupId
   * @returns
   */
  async getGroupById(groupId: number): Promise<{ group: GetAllGroups | null; error: PostgrestError | null }> {
    const { data, error } = await supabase
      .from("groups")
      .select(
        `
      id, 
      name,
      avatar,
      users,
      admins,
      roles
      `
      )
      .eq("id", groupId)
      .single();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to load group.",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_API: getGroupById");
      return { group: null, error };
    }

    return { group: data as unknown as GetAllGroups, error: null };
  }

  /**
   * Update group name
   *
   * @param groupId
   * @param newName
   * @returns
   */
  async updateGroupName(
    groupId: number,
    newName: string
  ): Promise<{ group: GroupRow | null; error: PostgrestError | null }> {
    const { data, error } = await supabase.from("groups").update({ name: newName }).eq("id", groupId).select().single();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to update group name.",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_API: updateGroupName");
      return { group: null, error };
    }

    return { group: data, error: null };
  }

  /**
   * Leave group
   *
   * @param groupId
   * @returns
   */
  async leaveGroup(groupId: number): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase.rpc("leave_group", { group_id: groupId });

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to leave group.",
          severity: "error",
        });
      }

      return { error };
    }

    return { error: null };
  }

  /**
   * Add or remove group admins
   *
   * @param groupId
   * @param newAdmins
   * @returns
   */
  async addOrRemoveGroupAdmins(
    groupId: number,
    newAdmins: number[]
  ): Promise<{ group: GroupRow | null; error: PostgrestError | null }> {
    const { data, error } = await supabase
      .from("groups")
      .update({ admins: newAdmins })
      .eq("id", groupId)
      .select()
      .single();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to add/remove admin(s).",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_API: addOrRemoveGroupAdmins");
      return { group: null, error };
    }

    return { group: data, error: null };
  }

  /**
   * Add or remove group users
   *
   * @param groupId
   * @param newUsers
   * @returns
   */
  async addOrRemoveGroupUsers(
    groupId: number,
    newUsers: number[],
    newAdmins?: number[]
  ): Promise<{ error: PostgrestError | null }> {
    const updatedData: { users: number[]; admins?: number[] } = {
      users: newUsers,
    };

    if (newAdmins) {
      updatedData.admins = newAdmins;
    }

    const { error } = await supabase.from("groups").update(updatedData).eq("id", groupId);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to add/remove user(s).",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_API: addOrRemoveGroupUsers");
      return { error };
    }

    return { error: null };
  }

  /**
   * Update group roles
   *
   * @param groupId
   * @param roles
   * @returns
   */
  async updateGroupRoles(groupId: number, roles: GroupRoles): Promise<{ error: PostgrestError | null }> {
    const { error } = await supabase
      .from("groups")
      .update({ roles } as unknown as DbGroup["Update"])
      .eq("id", groupId);

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to update roles.",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_API: updateGroupRoles");
      return { error };
    }

    return { error: null };
  }

  /**
   * Add or remove group admins
   *
   * @param groupId
   * @param avatar
   * @returns
   */
  async updateGroupAvatar(
    groupId: number,
    avatar: string
  ): Promise<{ group: GroupRow | null; error: PostgrestError | null }> {
    const { data, error } = await supabase.from("groups").update({ avatar }).eq("id", groupId).select().single();

    if (error) {
      if (this.showNotification) {
        this.showNotification({
          message: "Unable to update group avatar.",
          severity: "error",
        });
      }

      logSentryError(error, "GROUP_API: updateGroupAvatar");
      return { group: null, error };
    }

    return { group: data, error: null };
  }
}

export default GroupApi;
